"use client";

import React, { useState, useEffect } from "react";
import { Modal } from "@mui/material";
import { getTenantPosts, addTenantEmployee, addPost } from "../../components/http";
import { useTranslation } from "react-i18next";
import TextInput from "../../components/inputs/TextInput";
import SelectInput from "../../components/inputs/SelectInput";
import ButtonsGroup from "../../components/PopUps/ButtonsGroup";
import styles from "./EmployeePage.module.scss";

const EmployeeAddPopup = ({ open, onClose, onEmployeeAdded }) => {
  const { t } = useTranslation("");

  const [employeeData, setEmployeeData] = useState({
    fullname: "",
    post_id: "",
  });

  const [posts, setPosts] = useState([]);
  const [newPostName, setNewPostName] = useState("");
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const fetchedPosts = await getTenantPosts();
        setPosts(fetchedPosts || []);
      } catch (error) {
        console.error(t("employee.Add.errorLoadingPosts"), error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [t]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setEmployeeData((prev) => ({ ...prev, [name]: value }));

    // Clear error if the user starts typing
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const validateFields = () => {
    let newErrors = {};

    if (!employeeData.fullname.trim()) {
      newErrors.fullname = t("employee.Add.fullnameRequired");
    }

    if (!employeeData.post_id) {
      newErrors.post_id = t("employee.Add.postRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Return true if no errors
  };

  const handleAddPost = async () => {
    if (!newPostName.trim()) {
      alert(t("employee.Add.emptyPostName"));
      return;
    }

    try {
      const newPost = await addPost({ name: newPostName });
      setPosts((prevPosts) => [...prevPosts, newPost]);
      setNewPostName("");
      alert(t("employee.Add.postAddedSuccessfully"));
    } catch (error) {
      console.error(t("employee.Add.errorAddingPost"), error);
      alert(t("employee.Add.errorSavingPost"));
    }
  };

  const handleSave = async () => {
    if (!validateFields()) return; // Prevent submission if validation fails

    try {
      await addTenantEmployee(employeeData);
      alert(t("employee.Add.successMessage"));
      onEmployeeAdded();
      onClose();
    } catch (error) {
      console.error(t("employee.Add.errorAddingEmployee"), error);
      alert(t("employee.Add.errorSavingEmployee"));
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="employee-add-popup" className={styles.modalContainer}>
      <div className={styles.modalContent}>
        <h2 className={styles.title}>{t("employee.Add.title")}</h2>

        <div className={styles.inputsContainer}>
          <TextInput
            label={t("employee.Add.fullname")}
            name="fullname"
            value={employeeData.fullname}
            onChange={handleInputChange}
            placeholder={t("employee.Add.fullname")}
            required={true}
            error={!!errors.fullname}
            helperText={errors.fullname}
          />

          <SelectInput
            label={t("employee.Add.post")}
            name="post_id"
            value={employeeData.post_id}
            onChange={handleInputChange}
            options={posts.map((post) => ({ value: post.id, label: post.name || "Unnamed Post" }))} 
            required
          />



          <div className={styles.newPostContainer}>
            <TextInput
              label={t("employee.Add.newPostName")}
              value={newPostName}
              onChange={(e) => setNewPostName(e.target.value)}
              placeholder={t("employee.Add.newPostName")}
            />
            <button className={styles.addPostButton} onClick={handleAddPost}>
              {t("employee.Add.addPostButton")}
            </button>
          </div>
        </div>

        <ButtonsGroup
          onSave={handleSave}
          onCancel={onClose}
          saveText={t("employee.Add.save")}
          cancelText={t("employee.Add.cancel")}
        />
      </div>
    </Modal>
  );
};

export default EmployeeAddPopup;
